import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { Field, Input } from "@/components/common/Field";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!/^\+?[\d\s\-()]{8,18}$/.test(phone.trim())) errs.phone = "Enter a valid phone number with country code, e.g. +91 98765 43210";
    if (!/^\d{4,6}$/.test(pin)) errs.pin = "Invalid PIN — must be 4 to 6 digits";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const data = await login(phone.trim(), pin);
      navigate(data.user.role === "super_admin" ? "/platform" : "/");
    } catch (err) {
      setErrors({ form: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-start">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Car className="h-6 w-6" /></div>
          <h1 className="text-3xl font-bold tracking-tight">Insideman's Garage</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in with your phone number and PIN.</p>
        </div>
        <form onSubmit={submit} className="fv-card space-y-4 p-6" data-testid="login-form" noValidate>
          <Field label="Phone number" required error={errors.phone}>
            <Input data-testid="phone-input" type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 99999 00001" value={phone} onChange={(e) => setPhone(e.target.value)} error={!!errors.phone} />
          </Field>
          <Field label="PIN" required error={errors.pin} hint={!errors.pin && "4 to 6 digits"}>
            <div className="relative">
              <Input data-testid="pin-input" type={show ? "text" : "password"} inputMode="numeric" autoComplete="current-password" maxLength={6} placeholder="••••" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} error={!!errors.pin} className="pr-10" />
              <button type="button" data-testid="toggle-pin-visibility" onClick={() => setShow(!show)} className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground" aria-label={show ? "Hide PIN" : "Show PIN"}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            </div>
          </Field>
          {errors.form && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert" data-testid="login-error">{errors.form}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit-btn" className="h-10 w-full rounded-full bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-[#0071e3] disabled:opacity-50">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="mt-6 text-xs text-muted-foreground">Accounts are provisioned by your fleet Admin. Locked out? Ask an Admin to reset your PIN.</p>
      </div>
    </div>
  );
}
